import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import type { Locale } from '@/client/i18n';
import {
  buildCanonical,
  buildHreflangs,
  isProductionHost,
} from '@/client/lib/site';

type Props = {
  title: string;
  description?: string;
  locale: Locale;
  // Override canonical pathname; defaults to current location.
  pathname?: string;
  // Per-page robots override. Defaults to noindex on non-production hosts
  // and to index,follow on production. Pass `false` to force noindex
  // (used by /portal, /admin, checkout flows we don't want indexed).
  index?: boolean;
  // Optional Open Graph image. Defaults to a static brand OG.
  ogImage?: string;
  // Optional JSON-LD payload (object or array). Stringified and emitted
  // inside a <script type="application/ld+json"> tag.
  jsonLd?: object | object[];
};

const BRAND_NAME_SK = 'Akadémia Dajany Rodriguez';
const BRAND_NAME_CS = 'Akademie Dajany Rodriguez';
const DEFAULT_OG = '/brand/og-default.png';

export function Seo({
  title,
  description,
  locale,
  pathname,
  index,
  ogImage,
  jsonLd,
}: Props) {
  const location = useLocation();
  const path = pathname ?? location.pathname;
  const canonical = buildCanonical(path);
  const alternates = buildHreflangs(path);
  const brand = locale === 'cs' ? BRAND_NAME_CS : BRAND_NAME_SK;
  const fullTitle = title.includes(brand) ? title : `${title} | ${brand}`;
  const ogLocale = locale === 'cs' ? 'cs_CZ' : 'sk_SK';
  const og = ogImage ?? DEFAULT_OG;
  // Production-only indexing. Non-prod hosts (staging, previews) always
  // emit noindex. The `index` prop, when set, lets a route force noindex
  // even on production (portal, admin, checkout).
  const allowIndex = index !== false && isProductionHost();
  const robots = allowIndex ? 'index, follow' : 'noindex, nofollow';

  return (
    <Helmet>
      <html lang={locale} />
      <title>{fullTitle}</title>
      {description ? <meta name="description" content={description} /> : null}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="sk" href={alternates.sk} />
      <link rel="alternate" hrefLang="cs" href={alternates.cs} />
      <link rel="alternate" hrefLang="x-default" href={alternates.sk} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content={brand} />
      <meta property="og:image" content={og.startsWith('http') ? og : `${canonical.replace(path, '')}${og}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description ? <meta name="twitter:description" content={description} /> : null}
      {jsonLd ? (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      ) : null}
    </Helmet>
  );
}
